import { useCallback } from 'react'
import { useSignGrievanceUpload } from './backend/grievances'
import { writeErrorMessage } from '../utils/writeError'
import type { Attachment } from '../utils/grievances'

// 25 MB, the bucket's own limit. A WhatsApp voice note is well under a megabyte;
// a phone video of a car is not, and a person should not be told to compress
// the evidence of his own grievance.
export const MAX_GRIEVANCE_FILE = 25 * 1024 * 1024

/**
 * Two steps, not the three of useDisputeUpload: sign, then PUT. There is no
 * separate record step because the file becomes part of the grievance only when
 * the log form or a note is saved with it, and that call is what checks the
 * caller may see the grievance. A file that is uploaded and never saved is an
 * object nothing points at, invisible and harmless.
 */
export function useGrievanceUpload() {
  const sign = useSignGrievanceUpload()
  const signTrigger = sign.trigger
  const upload = useCallback(async (files: File[], grievanceId: string | null): Promise<{ done: Attachment[]; failed: string[] }> => {
    const done: Attachment[] = []
    const failed: string[] = []
    for (const file of files) {
      if (file.size > MAX_GRIEVANCE_FILE) { failed.push(`${file.name}: larger than 25 MB.`); continue }
      try {
        const signed = (await signTrigger({ grievance_id: grievanceId, filename: file.name }, { skipCache: true })) as { signedUrl?: string; path?: string } | null
        if (!signed?.signedUrl || !signed.path) throw new Error('No upload address came back.')
        const put = await fetch(signed.signedUrl, { method: 'PUT', headers: file.type ? { 'Content-Type': file.type } : undefined, body: file })
        if (!put.ok) throw new Error(`Storage refused the file (${put.status}).`)
        done.push({ bucket: 'grievance-files', path: signed.path, filename: file.name, mime: file.type || null })
      } catch (err) {
        failed.push(`${file.name}: ${writeErrorMessage(err)}`)
      }
    }
    return { done, failed }
  }, [signTrigger])
  return { upload, uploading: sign.loading }
}
